import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { useSubscription, useStartCheckout, useOpenPortal } from '@/billing/hooks';
import { errorMessage } from '@/lib/errorMessage';
import { useT } from '@/i18n/useT';

/**
 * Phase 4A-2 — subscribe / manage billing.
 *
 * Hosted Stripe: "Subscribe" → Checkout, "Manage" → the Billing Portal (invoices
 * / update card / cancel). No card data touches this app. After a successful
 * Checkout we poll until the webhook activates the subscription, then send the
 * customer to their dashboard (which the server now gates on an active plan).
 */
const FEATURE_KEYS = [
  'billing.feat1',
  'billing.feat2',
  'billing.feat3',
  'billing.feat4',
  'billing.feat5',
  'billing.feat6',
] as const;

function Check() {
  return (
    <svg className="billing-check" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M5 10.5l3.5 3.5L15 6.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function BillingPage() {
  const { t } = useT();
  const sub = useSubscription();
  const checkout = useStartCheckout();
  const portal = useOpenPortal();
  const [error, setError] = useState('');
  const [params] = useSearchParams();
  const checkoutFlag = params.get('checkout'); // 'success' | 'cancelled' after Stripe

  // After a successful Checkout, poll until the webhook flips us active, then
  // hand off to the dashboard. Gives up after ~90s (the page still works — it
  // just shows the active/Manage state).
  useEffect(() => {
    if (checkoutFlag !== 'success') return;
    let stopped = false;
    const tick = async () => {
      const r = await sub.refetch();
      if (!stopped && r.data?.active) window.location.assign('/');
    };
    void tick();
    const id = setInterval(tick, 3000);
    const giveUp = setTimeout(() => clearInterval(id), 90_000);
    return () => {
      stopped = true;
      clearInterval(id);
      clearTimeout(giveUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutFlag]);

  const fmtDate = (ms: number | null): string =>
    ms ? new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

  async function go(run: () => Promise<{ url: string }>): Promise<void> {
    setError('');
    try {
      const r = await run();
      window.location.assign(r.url); // hand off to Stripe's hosted page
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  if (sub.isLoading) {
    return (
      <Card title={t('billing.title')} subtitle={t('billing.subtitle')}>
        <p className="muted">{t('common.loading')}</p>
      </Card>
    );
  }

  const s = sub.data;
  if (s && !s.billingConfigured) {
    return (
      <Card title={t('billing.title')} subtitle={t('billing.subtitle')}>
        <Alert kind="info">{t('billing.notConfigured')}</Alert>
      </Card>
    );
  }

  const active = !!s?.active;
  const price = s?.priceUsd ?? 30;
  const isPastDue = s?.status === 'past_due';
  const isTrialing = s?.status === 'trialing';
  const trialDays = s?.trialDays ?? 0;
  const trialEligible = !!s?.trialEligible;
  const daysLeft = s?.currentPeriodEnd ? Math.max(0, Math.ceil((s.currentPeriodEnd - Date.now()) / 86_400_000)) : 0;

  return (
    <Card title={t('billing.title')} subtitle={t('billing.subtitle')}>
      {checkoutFlag === 'success' && <Alert kind="success">{t('billing.activating')}</Alert>}
      {checkoutFlag === 'cancelled' && <Alert kind="info">{t('billing.checkoutCancelled')}</Alert>}
      <Alert kind="error">{error}</Alert>

      <div className="billing-plan">
        <div className="billing-plan-name">{t('billing.planName')}</div>
        {trialEligible ? <div className="billing-trial-flag">{t('billing.trialFlag', { days: trialDays })}</div> : null}
        <div className="billing-price">
          <span className="billing-amount">${price}</span>
          <span className="billing-interval"> / {t('billing.month')}</span>
        </div>
        <p className="billing-tagline">
          {trialEligible ? t('billing.trialTagline', { days: trialDays }) : t('billing.tagline')}
        </p>
      </div>

      <ul className="billing-features">
        {FEATURE_KEYS.map((k) => (
          <li key={k}>
            <Check />
            <span>{t(k)}</span>
          </li>
        ))}
      </ul>

      {active ? (
        <>
          {isTrialing ? (
            <div className="billing-trial-live">
              <span className="billing-trial-dot" />
              <div>
                <div className="billing-trial-count">
                  {daysLeft <= 1 ? t('billing.trialLastDay') : t('billing.trialDaysLeft', { days: daysLeft })}
                </div>
                {s?.currentPeriodEnd ? (
                  <div className="billing-trial-charge muted">{t('billing.trialChargeOn', { date: fmtDate(s.currentPeriodEnd) })}</div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <Alert kind={isPastDue ? 'error' : 'success'}>
                {isPastDue ? t('billing.pastDue') : t('billing.active')}
              </Alert>
              {s?.currentPeriodEnd ? (
                <p className="muted">{t('billing.renewsOn', { date: fmtDate(s.currentPeriodEnd) })}</p>
              ) : null}
            </>
          )}
          <Button onClick={() => go(() => portal.mutateAsync())} busy={portal.isPending}>
            {portal.isPending ? t('billing.opening') : t('billing.manageCta')}
          </Button>
        </>
      ) : (
        <>
          {s?.status === 'canceled' ? <Alert kind="info">{t('billing.canceled')}</Alert> : null}
          {trialEligible ? <Alert kind="success">{t('billing.trialBanner', { days: trialDays })}</Alert> : null}
          <Button onClick={() => go(() => checkout.mutateAsync())} busy={checkout.isPending}>
            {checkout.isPending
              ? t('billing.subscribing')
              : trialEligible
                ? t('billing.startTrialCta')
                : t('billing.subscribeCta')}
          </Button>
          <p className="billing-fineprint muted">
            {trialEligible ? t('billing.trialFineprint', { days: trialDays, price }) : t('billing.fineprint')}
          </p>
        </>
      )}
    </Card>
  );
}
